import { Pdf, SinglePlanSidePanel, TooltipBlock } from '@/@types/content';
import PanelContentWithErrorBoundary from '@/components/blocks/ErrorFallback/PanelContentWithErrorBoundary';
import ContentArea from '@/components/cms/ContentArea';
import Button from '@/components/ui/Buttons/Button';
import Icon from '@/components/ui/Icon';
import Link from '@/components/ui/Link';
import Panel, { PanelProps } from '@/components/ui/Panel';
import ComplexTooltip from '@/components/ui/Tooltip/ComplexTooltip';
import { ScreenSizeQueries } from '@/constants/breakpoints';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import useToast from '@/hooks/useToast';
import {
  useLazyDeleteCreditPlanQuery,
  useRemoveAllFromLibraryMutation,
  useSaveAllToLibraryMutation,
  useResendCreditPlanEmailMutation,
  ResendCreditPlanEmailRequest,
  useSubmitCreditPlanFeedbackMutation
} from '@/redux/api/client/creditPlan';
import { useRemoveFromLibraryMutation } from '@/redux/api/client/library';
import { updatePlanSavedItems, upsertPlan } from '@/redux/slices/creditPlanSlice';
import { parseLiveProgramDate } from '@/utils/dateHelpers';
import { templateString } from '@/utils/helpers';
import classnames from 'classnames';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import CardBuilder from '../../Cards/CardBuilder';
import Divider from '../../Divider';
import DropdownMenu from '../../DropdownMenu';
import { getIsSavedToLibrary, isLiveProgram } from '../../SearchResults/Snippets/helpers';
import { SearchableType } from '../../SearchResults/searchableTypeToSnippetVariant';
import { ThumbnailProps } from '../../Thumbnail';
import CircleButton from '../../Buttons/CircleButton';
import { DEFAULT_AI_POWERED_TEXT } from '../util/constants';
import { selectCartOpenStatus } from '@/redux/selectors/cartSelectors';
import { useAppSelector } from '@/hooks';
import { useGetCreditPlan } from '@/contexts/creditPlan';
import { expandContent } from '@/lib/helpers/expandChildren';
import useCreditPlanPdf from '../Pdf/useCreditPlanPdf';
import type { RichTextBlock as RichTextBlockProps } from '@/@types/content';
import DebugInformation from '../DebugInformation';
import InlineDebugInformation from '../InlineDebugInformation';
import CreditPlanFailureSidePanelContent from './CreditPlanFailureSidePanelContent';
import {
  CreditPlanFailureReason,
  CreditPlanJurisdictionAlertMessage,
  CreditPlanResultItem,
  ItemAnalyticsModel,
  ItemListAnalyticsModel
} from '@/@types/client-api';
import DebugButtons from '../DebugButtons';
import { IContent } from '@/@types/cms';
import AnnouncementAlertBlock from '@/components/blocks/AnnouncementAlertBlock';
import { useAnalytics } from '@/analytics/hooks/useAnalytics';
import { AnalyticsContext, AnalyticsEventName } from '@/analytics/constants';
import { ConvertItemAnalyticsModelBase } from '@/analytics/analyticsUtils';
import { Currency } from '@/constants/currency';
import ThumbFeedbackButtons, {
  ThumbFeedbackValue
} from '@/components/ui/Feedback/ThumbFeedbackButtons';
import RichText from '@/components/cms/RichText';
import {
  trackAddWishListEvent,
  trackBulkRemoveWishListEvent,
  trackRemoveWishListEvent,
  trackSelectItem,
  trackViewItemListEvent
} from '@/analytics/ecommerce';
import { format } from 'date-fns';

export interface GeneratedCreditPlanProps extends Omit<PanelProps, 'size' | 'children'> {
  creditPlanLoadFailureContent?: IContent[];
  jurisdictionId?: string;
  jurisdictionName?: string;
  aiPoweredText?: React.ReactNode;
  icon?: string;
  content?: SinglePlanSidePanel & {
    alerts?: CreditPlanJurisdictionAlertMessage[];
  };
  pdfContent?: Pdf;
  debugModeEnabled?: boolean;
  openJurisdictionBrowseUrl?: string;
  onBackButtonPress?: () => void;
}

const GeneratedCreditPlanSidePanel: React.FC<GeneratedCreditPlanProps> = ({
  creditPlanLoadFailureContent,
  jurisdictionName,
  jurisdictionId,
  aiPoweredText = DEFAULT_AI_POWERED_TEXT,
  icon,
  pdfContent,
  debugModeEnabled,
  openJurisdictionBrowseUrl,
  onBackButtonPress,
  content: {
    heading: rawHeading,
    subheading,
    creditBreakdownTooltip: creditBreakdownTooltipBlock,
    messagingContentArea,
    buttonText,
    alerts
  } = {},
  ...panelProps
}) => {
  const dispatch = useDispatch();
  const [isDeleting, setIsDeleting] = useState<boolean>();
  const [emailResent, setEmailResent] = useState<boolean>(false);
  const [isResendingEmail, setIsResendingEmail] = useState<boolean>(false);
  const [isDownloadingPlan, setIsDownloadingPlan] = useState<boolean>(false);
  const [planItem] = useGetCreditPlan(jurisdictionId);
  const cartPanelIsOpen = useAppSelector(selectCartOpenStatus);
  const { trackEvent } = useAnalytics();

  const { showSuccessMessage, showFailMessage } = useToast();
  const [contentContainer, setContentContainer] = useState<HTMLDivElement | null>(null);
  const [savingItems, setSavingItems] = useState<Record<string, boolean>>({});

  const [feedbackByPlan, setFeedbackByPlan] = useState<Record<string, ThumbFeedbackValue>>({});
  const [submitCreditPlanFeedback] = useSubmitCreditPlanFeedbackMutation();

  const planId = useMemo(
    () => planItem?.processId ?? planItem?.creditPlan?.id ?? undefined,
    [planItem?.processId, planItem?.creditPlan?.id]
  );

  const [saveAllToLibrary] = useSaveAllToLibraryMutation();
  const [removeAllToLibrary] = useRemoveAllFromLibraryMutation();
  const [removeSingleFromLibrary] = useRemoveFromLibraryMutation();
  const [deletePlan] = useLazyDeleteCreditPlanQuery();
  const [resendEmail] = useResendCreditPlanEmailMutation();

  const isXs = useMediaQuery(ScreenSizeQueries.xsOnly);

  const isSaving = useMemo(() => !!Object.values(savingItems).some(x => !!x), [savingItems]);
  const debugInformation = useMemo(() => planItem?.debugInformation, [planItem?.debugInformation]);
  const plan = useMemo(() => planItem?.creditPlan, [planItem?.creditPlan]);

  const responseFeedbackSent = Boolean(planItem?.responseFeedbackSent);
  const currentPlanFeedback = planId ? (feedbackByPlan[planId] ?? null) : null;
  const isFeedbackDisabled = responseFeedbackSent || Boolean(planId && feedbackByPlan[planId]);
  const resolvedJurisdiction = useMemo(
    () => jurisdictionName || jurisdictionId || 'unknown',
    [jurisdictionId, jurisdictionName]
  );

  const openHotjarCreditPlanSurvey = useCallback(() => {
    if (typeof window === 'undefined') return;

    trackEvent(AnalyticsEventName.FeedbackCreditplan, {
      context: AnalyticsContext.CreditPlanPanel,
      jurisdiction: resolvedJurisdiction
    });

    if (typeof window.hj !== 'function') return;

    window.hj('event', 'creditplan_feedback');
  }, [resolvedJurisdiction, trackEvent]);

  const handleFeedback = useCallback(
    async (value: ThumbFeedbackValue) => {
      if (isFeedbackDisabled || !planId) return;

      trackEvent(AnalyticsEventName.RateCreditplan, {
        context: AnalyticsContext.CreditPlanPanel,
        jurisdiction: resolvedJurisdiction,
        method: value === 'thumbsUp' ? 'Positive' : 'Negative'
      });

      try {
        await submitCreditPlanFeedback({ planId, thumbValue: value }).unwrap();

        setFeedbackByPlan(prev => ({
          ...prev,
          [planId]: value
        }));

        if (planItem && jurisdictionId) {
          dispatch(
            upsertPlan({
              jurisdiction: jurisdictionId,
              plan: {
                ...planItem,
                responseFeedbackSent: true
              }
            })
          );
        }

        if (value === 'thumbsUp') {
          const message = (
            <span className="text-2">
              Response submitted - thank you!{' '}
              <button className="underline" onClick={openHotjarCreditPlanSurvey}>
                Leave feedback
              </button>
            </span>
          );

          showSuccessMessage(message);
        } else {
          showSuccessMessage('Response submitted - thank you!');
          openHotjarCreditPlanSurvey();
        }
      } catch {
        showFailMessage('Failed to send feedback');
      }
    },
    [
      dispatch,
      isFeedbackDisabled,
      jurisdictionId,
      planId,
      planItem,
      resolvedJurisdiction,
      showFailMessage,
      showSuccessMessage,
      submitCreditPlanFeedback,
      trackEvent,
      openHotjarCreditPlanSurvey
    ]
  );

  // Swap side panels to prevent layering
  const isOpen = useMemo(
    () => panelProps.isOpen && !cartPanelIsOpen,
    [cartPanelIsOpen, panelProps.isOpen]
  );

  const itemListAnalyticsModel = useMemo<ItemListAnalyticsModel>(() => {
    return {
      listId: `${jurisdictionName}`,
      listName: `CreditPlan`
    };
  }, [jurisdictionName]);

  // Reset email resend states when panel is closed
  useEffect(() => {
    if (!isOpen) {
      setEmailResent(false);
      setIsResendingEmail(false);
    }
  }, [isOpen]);

  const allItemsSaved = useMemo(() => {
    return (
      plan?.creditProductGroups
        ?.flatMap(a => a.searchResultItems)
        ?.every(a => a?.isSavedToUserLibrary) ?? false
    );
  }, [plan?.creditProductGroups]);

  const getCreditPlanHeadingParts = (rawHeading?: string, jurisdictionName?: string) => {
    const [callout, ...rest] = rawHeading?.split('\n') ?? [];

    const content = templateString(
      rest.join('\n'),
      {
        License: jurisdictionName
      },
      true
    );

    return {
      callout,
      content,
      text: [callout, content].filter(Boolean).join(' ')
    };
  };

  const headingData = useMemo(() => {
    return getCreditPlanHeadingParts(rawHeading, jurisdictionName);
  }, [rawHeading, jurisdictionName]);

  const heading = useMemo(() => {
    return (
      <>
        <span className="bg-gradient-full bg-clip-text text-transparent">
          {headingData.callout}
        </span>
        <br />
        {headingData.content}
      </>
    );
  }, [headingData]);

  const { triggerDownload } = useCreditPlanPdf({
    heading: rawHeading ?? '',
    creditBreakdown: plan?.creditBreakdown ?? {},
    productGroups: plan?.creditProductGroups ?? [],
    content: pdfContent,
    betaIcon: !!icon,
    disclaimer: expandContent(messagingContentArea)?.filter(a => !!(a as RichTextBlockProps))?.[0],
    aiPoweredText,
    jurisdictionName,
    subheading,
    openJurisdictionBrowseUrl
  });

  const creditBreakdownTooltip = useMemo(() => {
    const tooltip = creditBreakdownTooltipBlock?.[0]?.contentLink?.expanded as TooltipBlock;
    if (!tooltip) return null;

    return {
      children: (
        <>
          <ComplexTooltip.Trigger className="z-1">
            <Icon name="info" size="small" className="text-gray" />
          </ComplexTooltip.Trigger>

          <ComplexTooltip.Content customContainer={contentContainer}>
            <div>
              {tooltip.title && <p className="text-2 mb-1 font-bold">{tooltip.title}</p>}
              {tooltip.copy && <p className="text-2 mb-2">{tooltip.copy}</p>}
              {tooltip.link && (
                <Link
                  href={tooltip.link?.href}
                  text={tooltip.link?.text}
                  title={tooltip.link?.title}
                  target={tooltip.link?.target}
                />
              )}
            </div>
          </ComplexTooltip.Content>
        </>
      )
    };
  }, [contentContainer, creditBreakdownTooltipBlock]);

  const toggleIsSaving = useCallback((pks: string[], state: boolean) => {
    setSavingItems(prev => pks.reduce((acc, pk) => ({ ...acc, [pk]: state }), prev));
  }, []);

  const updateSavedItems = useCallback(
    (records: Record<string, boolean>) => {
      dispatch(
        updatePlanSavedItems({
          jurisdiction: jurisdictionId!,
          items: records
        })
      );
      return true;
    },
    [dispatch, jurisdictionId]
  );

  const _saveToLibrary = useCallback(
    async (pks: string[]) => {
      if (!pks?.length) return {};

      toggleIsSaving(pks, true);

      const { data, error } = await saveAllToLibrary({
        codes: pks,
        license: jurisdictionId!
      });

      const success = pks.every(k => !!data?.[k]) && !error;

      if (success && data) updateSavedItems(data);

      toggleIsSaving(pks, false);

      return data;
    },
    [jurisdictionId, saveAllToLibrary, toggleIsSaving, updateSavedItems]
  );

  const handleSaveAllToLibrary = useCallback(async () => {
    const pks =
      plan?.creditProductGroups
        ?.flatMap(a => a.searchResultItems)
        ?.map(a => a?.variationPk)
        ?.filter((a): a is string => !!a) ?? [];

    // Track save_creditplan event
    trackEvent(AnalyticsEventName.SaveCreditPlan, {
      context: AnalyticsContext.CreditPlanPanel,
      jurisdiction: resolvedJurisdiction
    });

    // Prepare items for add_to_wishlist event
    const items =
      plan?.creditProductGroups
        ?.flatMap(group => group.searchResultItems ?? [])
        ?.filter(item => item?.itemAnalyticsModel)
        ?.map((item, index) =>
          ConvertItemAnalyticsModelBase(item.itemAnalyticsModel, undefined, index)
        ) ?? [];

    // Standardize item_list_name by converting to lowercase and replacing spaces with underscores
    const standardizedListName = `credit_plan_panel_${resolvedJurisdiction.toLowerCase().replace(/\s+/g, '_')}`;

    // Track add_to_wishlist event with ecommerce data
    trackEvent(AnalyticsEventName.AddToWishlist, {
      context: AnalyticsContext.CreditPlanPanel,
      ecommerce: {
        save_method: 'save_all_button',
        item_list_name: standardizedListName,
        value: 0,
        currency: Currency.USD,
        items
      }
    });

    const res = await _saveToLibrary(pks);

    if (pks?.some(k => !!res?.[k])) {
      showSuccessMessage('All programs saved to library');
      return;
    }

    showFailMessage('Failed to add all programs to library');
  }, [
    _saveToLibrary,
    plan?.creditProductGroups,
    resolvedJurisdiction,
    showFailMessage,
    showSuccessMessage,
    trackEvent
  ]);

  const handleRemoveAllToLibrary = useCallback(async () => {
    const allPlanItems = plan?.creditProductGroups?.flatMap(a => a.searchResultItems ?? []) ?? [];
    const pks = allPlanItems.map(a => a?.variationPk).filter((a): a is string => !!a);

    if (!pks?.length) return false;

    toggleIsSaving(pks, true);

    const { data, error } = await removeAllToLibrary({
      codes: pks,
      license: jurisdictionId!
    });

    toggleIsSaving(pks, false);

    if (!error && data) {
      if (pks?.some(k => !!data?.[k])) {
        const removedItems = allPlanItems.filter(
          item => !!item?.variationPk && !!data[item.variationPk]
        );
        const removedItemAnalytics = removedItems
          .map(item => item.itemAnalyticsModel)
          .filter((item): item is ItemAnalyticsModel => !!item);

        removedItemAnalytics.forEach(item => {
          trackRemoveWishListEvent(
            item,
            AnalyticsContext.CreditPlanPanel,
            true,
            itemListAnalyticsModel
          );
        });

        trackBulkRemoveWishListEvent(
          removedItemAnalytics,
          AnalyticsContext.CreditPlanPanel,
          itemListAnalyticsModel
        );

        showSuccessMessage(
          <span className="flex flex-row items-center justify-start">
            <span className="mr-2">All programs removed from library.</span>
            <a
              onClick={async () => await handleSaveAllToLibrary()}
              href="#"
              className="text-2 underline hover:text-2-bold hover:text-white"
            >
              Undo
            </a>
          </span>
        );
        return updateSavedItems(
          Object.entries(data).reduce((acc, [k, v]) => ({ ...acc, [k]: !v }), {})
        );
      }
    }

    showFailMessage('Failed to remove all programs from library');
  }, [
    plan?.creditProductGroups,
    toggleIsSaving,
    removeAllToLibrary,
    itemListAnalyticsModel,
    jurisdictionId,
    showFailMessage,
    showSuccessMessage,
    updateSavedItems,
    handleSaveAllToLibrary
  ]);

  const handleSingleSaveToLibrary = useCallback(
    async (item?: CreditPlanResultItem | null) => {
      if (item?.variationPk) {
        const result = await _saveToLibrary([item?.variationPk]);

        if (result?.[item?.variationPk]) {
          showSuccessMessage('Program added to library');
          trackAddWishListEvent(
            item?.itemAnalyticsModel,
            AnalyticsContext.CreditPlanPanel,
            itemListAnalyticsModel
          );
          return true;
        }
      }
      showFailMessage('Failed to add program to library');
      return false;
    },
    [_saveToLibrary, itemListAnalyticsModel, showFailMessage, showSuccessMessage]
  );

  const handleSingleRemoveFromLibrary = useCallback(
    async (item?: CreditPlanResultItem | null) => {
      if (item?.variationPk) {
        toggleIsSaving([item?.variationPk], true);
        const { data, error } = await removeSingleFromLibrary(item?.variationPk);
        toggleIsSaving([item?.variationPk], false);

        if (!!data && !error) {
          trackRemoveWishListEvent(
            item.itemAnalyticsModel,
            AnalyticsContext.CreditPlanPanel,
            false,
            itemListAnalyticsModel
          );

          showSuccessMessage(
            <span className="flex flex-row items-center justify-start">
              <span className="mr-2">Program removed from library.</span>
              <a
                onClick={async () => await handleSingleSaveToLibrary(item)}
                href="#"
                className="text-2 underline hover:text-2-bold hover:text-white"
              >
                Undo
              </a>
            </span>
          );
          return updateSavedItems({ [item?.variationPk]: false });
        }
      }
      showFailMessage('Failed to remove program from library');
    },
    [
      handleSingleSaveToLibrary,
      removeSingleFromLibrary,
      showFailMessage,
      showSuccessMessage,
      toggleIsSaving,
      itemListAnalyticsModel,
      updateSavedItems
    ]
  );

  const deleteCreditPlan = useCallback(async () => {
    const id = planItem?.processId ?? planItem?.creditPlan?.id;

    if (id) {
      setIsDeleting(true);
      const { isError, data } = await deletePlan(id);
      setIsDeleting(false);

      if (!isError && !!data) {
        if (onBackButtonPress) onBackButtonPress();
        else if (panelProps?.onOpenChange) panelProps?.onOpenChange?.(false);
      } else {
        showFailMessage('Failed to delete plan');
      }
    }
  }, [
    deletePlan,
    onBackButtonPress,
    panelProps,
    planItem?.creditPlan?.id,
    planItem?.processId,
    showFailMessage
  ]);

  const menuButtonTrackEvent = useCallback(
    (event: AnalyticsEventName) => {
      trackEvent(event, {
        jurisdiction: resolvedJurisdiction
      });
    },
    [resolvedJurisdiction, trackEvent]
  );

  const handleResendEmail = useCallback(async () => {
    if (emailResent || isResendingEmail) {
      return;
    }

    const processId = planItem?.processId;
    const licenseIdentifier = planItem?.licenseIdentifier ?? jurisdictionId;

    // Check if both are missing
    if (!processId && !licenseIdentifier) {
      showFailMessage('Email not queued. Missing required information.');
      return;
    }

    // Build payload: use processId if available, otherwise use licenseIdentifier
    const payload: ResendCreditPlanEmailRequest = processId
      ? { processId }
      : { processId: '', licenseIdentifier };

    setIsResendingEmail(true);
    const { error, data } = await resendEmail(payload);
    setIsResendingEmail(false);

    if (!error && (data?.status === 'Pending' || !data?.status)) {
      setEmailResent(true);
      menuButtonTrackEvent(AnalyticsEventName.EmailCreditplan);
      showSuccessMessage('Email queued for resend. Check your inbox shortly');
    } else {
      showFailMessage('Failed to queue email. Please try again.');
    }
  }, [
    emailResent,
    isResendingEmail,
    planItem?.processId,
    planItem?.licenseIdentifier,
    jurisdictionId,
    resendEmail,
    showFailMessage,
    menuButtonTrackEvent,
    showSuccessMessage
  ]);

  const handleDownloadPDF = useCallback(async () => {
    setIsDownloadingPlan(true);
    await triggerDownload();
    menuButtonTrackEvent(AnalyticsEventName.DownloadCreditplan);
    setIsDownloadingPlan(false);
  }, [menuButtonTrackEvent, triggerDownload]);

  const saveButton = useMemo(() => {
    const _buttonText = allItemsSaved ? 'Saved to library' : buttonText;
    const callback = allItemsSaved ? handleRemoveAllToLibrary : handleSaveAllToLibrary;
    const icon = allItemsSaved ? 'checkmark' : 'add';
    return (
      <Button
        iconLeft={icon}
        size="large"
        label={_buttonText}
        onClick={callback}
        disabled={!!isSaving || !!isDeleting}
      />
    );
  }, [
    allItemsSaved,
    buttonText,
    handleRemoveAllToLibrary,
    handleSaveAllToLibrary,
    isDeleting,
    isSaving
  ]);

  const dropdownMenu = useMemo(() => {
    return (
      <DropdownMenu
        menuStyle="standalone-dark"
        button={() => (
          <Button
            iconLeft="more-ios"
            size="large"
            aria-label="More options"
            color="outline-black"
          />
        )}
        items={[
          {
            label: emailResent ? 'Email resent' : 'Re-send email',
            onClick: handleResendEmail,
            icon: 'email',
            isLoading: isResendingEmail,
            disabled: emailResent || isResendingEmail
          },
          {
            label: 'Download copy',
            onClick: handleDownloadPDF,
            icon: 'download',
            isLoading: isDownloadingPlan,
            disabled: isDownloadingPlan
          }
        ]}
        menuClassName="mb-2 sm:!border-gray"
      />
    );
  }, [emailResent, handleResendEmail, isResendingEmail, handleDownloadPDF, isDownloadingPlan]);

  const selectItemDispatchEvent = useCallback(
    (card: Record<string, unknown>) => {
      const variationPk = card?.variationPk as string;

      const item = plan?.creditProductGroups
        ?.flatMap(group => group.searchResultItems ?? [])
        ?.find(i => i?.variationPk === variationPk);

      trackSelectItem(
        item?.itemAnalyticsModel,
        AnalyticsContext.CreditPlanPanel,
        itemListAnalyticsModel.listId || '',
        itemListAnalyticsModel.listName || ''
      );
    },
    [itemListAnalyticsModel, plan?.creditProductGroups]
  );

  const onCardItemClick = useCallback(
    (card: Record<string, unknown>) => {
      selectItemDispatchEvent(card);
      panelProps.onOpenChange?.(false);
    },
    [panelProps, selectItemDispatchEvent]
  );

  const planMetadata = useMemo(() => {
    if (planItem && planItem.isBetaMode) {
      return (
        <div className="label my-6 inline-flex w-fit flex-col font-semibold text-gray-dark">
          <span>{headingData.content}</span>
          {!!planId && (
            <span>
              credit plan id:&nbsp;<i>{planId}</i>
            </span>
          )}
          {!!planItem.generatedAt && (
            <span>
              generated at:&nbsp;<i>{format(planItem.generatedAt, 'M/d/yyyy HH:MMaaa')}</i>
            </span>
          )}
        </div>
      );
    }
  }, [headingData.content, planId, planItem]);

  const renderedList = useMemo(() => {
    return plan?.creditProductGroups?.map((group, i) => (
      <div key={i} className="mb-10 mt-16">
        <h6 className="heading-6-medium mb-2">{group.heading}</h6>
        <p className="text-2 text-gray-dark">{group.totalTimeText}</p>

        <Divider color="light" className="my-8" />

        <div className="flex flex-col">
          {group.searchResultItems?.map((item, i) => {
            const isProgramLive = isLiveProgram(item.searchableType as SearchableType);
            const thumbnail: Partial<ThumbnailProps> = {
              ratio: 1.5,
              mini: false,
              isListFormat: true,
              datesVariant: 'slim',
              showProgress: true,
              progressValue: item.playbackProgressPct
            };
            if (isProgramLive) {
              thumbnail.icon = null;
              thumbnail.tagsPosition = 'top';
              thumbnail.dates = parseLiveProgramDate(item.datesFormatted);
            }
            if (isXs) {
              thumbnail.ratio = 1;
            }
            const isSavedInLibrary = !!item.userList?.some(
              x => x.pk === item.variationPk && getIsSavedToLibrary(x)
            );
            const isItemSaving = !!savingItems?.[item.variationPk ?? ''] || isDeleting;

            return (
              <Fragment key={item.variationPk}>
                <CardBuilder
                  key={item.variationPk}
                  cardProps={item}
                  linkClickTarget="_self"
                  clickOnCardContent={onCardItemClick}
                  itemAnalyticsModel={item.itemAnalyticsModel}
                  itemListAnalyticsModel={itemListAnalyticsModel}
                  context={AnalyticsContext.CreditPlanPanel}
                  extraActions={
                    <CircleButton
                      icon={isSavedInLibrary ? 'checkmark' : 'add'}
                      isLoading={isItemSaving}
                      aria-current
                      label={isSavedInLibrary ? 'Saved to library' : 'Save to library'}
                      onClick={
                        isSavedInLibrary
                          ? () => handleSingleRemoveFromLibrary(item)
                          : () => handleSingleSaveToLibrary(item)
                      }
                      disabled={isItemSaving}
                    />
                  }
                  thumbnail={thumbnail}
                  useNaturalPhrasing
                  useSidePanelCard="credit-plan"
                />
                {!!debugModeEnabled && !!debugInformation?.allowed && (
                  <InlineDebugInformation
                    credits={item.credits ?? []}
                    jurisdictionId={jurisdictionId ?? ''}
                    className="mt-6"
                    pk={item.variationPk ?? ''}
                    isPersonalizedItem={item.isPersonalizedItem}
                  />
                )}

                {i < group.searchResultItems!.length - 1 && (
                  <Divider color="light" className="my-8" />
                )}
              </Fragment>
            );
          })}
        </div>
      </div>
    ));
  }, [
    plan?.creditProductGroups,
    isXs,
    savingItems,
    isDeleting,
    onCardItemClick,
    itemListAnalyticsModel,
    debugModeEnabled,
    debugInformation?.allowed,
    jurisdictionId,
    handleSingleRemoveFromLibrary,
    handleSingleSaveToLibrary
  ]);

  const planAlertMessages = useMemo(() => {
    if (alerts?.length) {
      return (
        <div className="my-6">
          {alerts?.map((a, i) => {
            return (
              <AnnouncementAlertBlock
                key={`${jurisdictionId}-alert-${i}`}
                copy={a.copy ?? ''}
                style={a.style}
              />
            );
          })}
        </div>
      );
    }
  }, [alerts, jurisdictionId]);

  const creditBreakdownSection = useMemo(() => {
    const jurisdictionCreditPlanTag = planItem?.jurisdictionCreditPlanTag?.trim();
    const jurisdictionOverviewMessage = planItem?.jurisdictionOverviewMessage?.trim();

    return (
      <div className="flex flex-col gap-3 rounded-[4px] bg-gray-feather p-5">
        <div className="flex items-center justify-between gap-3">
          <div className="text-1-medium inline-flex min-w-0 items-center gap-1 text-black">
            <span>{plan?.creditBreakdown?.total ?? 0} planned credits</span>
            {!!jurisdictionCreditPlanTag && (
              <span className="label inline-flex h-6 items-center justify-center rounded border border-gray px-2 text-black">
                {jurisdictionCreditPlanTag}
              </span>
            )}
            {creditBreakdownTooltip && <ComplexTooltip {...creditBreakdownTooltip} />}
          </div>

          {openJurisdictionBrowseUrl ? (
            <Link
              href={openJurisdictionBrowseUrl}
              text="See requirements"
              icon="chevron-right"
              variant="bold-link-with-icon"
              className="whitespace-nowrap text-black"
            />
          ) : (
            <span className="text-2-bold inline-flex items-center gap-1 whitespace-nowrap text-black">
              See requirements
              <Icon name="chevron-right" size="medium" />
            </span>
          )}
        </div>

        {!!jurisdictionOverviewMessage && (
          <RichText content={jurisdictionOverviewMessage} className="text-2 text-gray-darker" />
        )}
      </div>
    );
  }, [
    plan?.creditBreakdown?.total,
    creditBreakdownTooltip,
    openJurisdictionBrowseUrl,
    planItem?.jurisdictionCreditPlanTag,
    planItem?.jurisdictionOverviewMessage
  ]);

  useEffect(() => {
    if (!isOpen) return;
    const itemsAnalitics = plan?.creditProductGroups
      ?.flatMap(
        group => group.searchResultItems?.map(resultItem => resultItem.itemAnalyticsModel) ?? []
      )
      .filter((item): item is ItemAnalyticsModel => item !== undefined);

    if (itemsAnalitics && itemsAnalitics.length > 0) {
      trackEvent(AnalyticsEventName.LoadCreditplan, {
        context: AnalyticsContext.CreditPlanPanel,
        jurisdiction: resolvedJurisdiction
      });

      trackViewItemListEvent(
        itemListAnalyticsModel.listId || '',
        itemListAnalyticsModel.listName || '',
        itemsAnalitics,
        AnalyticsContext.CreditPlanPanel
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, resolvedJurisdiction]);

  return (
    <Panel
      {...{
        ...panelProps,
        isOpen
      }}
      size="medium"
    >
      <Panel.Content onBackButtonPress={onBackButtonPress}>
        <PanelContentWithErrorBoundary
          fallback={
            <CreditPlanFailureSidePanelContent
              failureReason={CreditPlanFailureReason.GENERAL}
              debugInformation={debugInformation}
              debugModeEnabled={!!debugModeEnabled}
              planId={planItem?.processId ?? undefined}
              creditPlanLoadFailureContent={creditPlanLoadFailureContent}
            />
          }
        >
          <Panel.Header>
            <div>
              <h3 className="text-2-medium mb-2 flex items-center justify-start gap-1 text-gray-dark">
                <Icon name="ai-tool" size="small" />
                <span className="mr-1">{aiPoweredText}</span>
                {icon && (
                  <Icon
                    name={icon}
                    size={icon === 'beta' ? 'xx-large' : 'large'}
                    className={classnames(icon === 'beta' && '-my-2')}
                  />
                )}
              </h3>
              <h2 className="heading-2">{heading}</h2>
              {subheading && (
                <p className="text-2 mt-5 font-normal">{templateString(subheading, {}, true)}</p>
              )}
            </div>
          </Panel.Header>
          <Panel.Body scrollClassName="!pt-6">
            <>
              {planAlertMessages}
              {creditBreakdownSection}
              {renderedList}
              {!!debugModeEnabled && (
                <div className="mb-4 mt-12">
                  <Divider color="light" className="w-full" />
                  <DebugInformation {...debugInformation} planId={planItem?.processId ?? ''} />
                </div>
              )}
              <ThumbFeedbackButtons
                value={currentPlanFeedback}
                onChange={handleFeedback}
                disabled={isFeedbackDisabled}
                containerClassName="mb-4 mr-auto mt-12 flex items-center gap-2"
                disabledClassName="cursor-not-allowed opacity-50 grayscale"
              />
              {planMetadata}
              <ContentArea
                components={expandContent(messagingContentArea)}
                propertyName="MessagingContentArea"
                className="label mb-6 font-medium text-gray-dark"
              />
            </>
            <div ref={setContentContainer} />
          </Panel.Body>

          <Panel.Footer divider="line" className="flex justify-end gap-5">
            <DebugButtons
              allowed={!!debugInformation?.allowed}
              isActive={!!debugModeEnabled}
              planId={planItem?.processId ?? undefined}
              onDeletePress={deleteCreditPlan}
              isDeleting={!!isDeleting || !!isSaving}
              planItem={planItem ?? undefined}
            />
            {dropdownMenu}
            {saveButton}
          </Panel.Footer>
        </PanelContentWithErrorBoundary>
      </Panel.Content>
    </Panel>
  );
};

export default GeneratedCreditPlanSidePanel;
